// Browser-authorized saves in a player-selected folder, with JSON export/import
// fallback. The browser cannot create an arbitrary folder without user consent.
const DB = "em2c-player-save-handles";
const STORE = "handles";
const HANDLE_KEY = "selected-game-folder";
const FILE = "Mars_Colony_Autosave.json";
const manualFile = () => `Mars_Colony_${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
const SAVE_KEYS = [
  "mars_em2c_save_v1", "mars_em2c_autosave_v1",
  "mars_em2c_systems_v2", "mars_em2c_systems_autosave_v2",
];

let folderHandle = null;
let writing = Promise.resolve();

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function rememberHandle(handle) {
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(handle, HANDLE_KEY);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
async function restoreHandle() {
  if (folderHandle) return folderHandle;
  const db = await openDB();
  const handle = await new Promise((resolve, reject) => {
    const req = db.transaction(STORE).objectStore(STORE).get(HANDLE_KEY);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  db.close();
  folderHandle = handle || null;
  return folderHandle;
}
function snapshot() {
  const saves = Object.fromEntries(SAVE_KEYS.map(key => [key, localStorage.getItem(key)]));
  return { format: "EM2C-MARS-COLONY", version: 1, exportedAt: new Date().toISOString(), saves };
}
export function downloadColonySave() {
  const blob = new Blob([JSON.stringify(snapshot(), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url; link.download = manualFile(); link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
async function writeSelectedFolder() {
  const directory = folderHandle || await restoreHandle();
  if (!directory) return false;
  if (typeof directory.queryPermission === "function" &&
      await directory.queryPermission({ mode: "readwrite" }) !== "granted") return false;
  const file = await directory.getFileHandle(FILE, { create: true });
  const writer = await file.createWritable();
  await writer.write(JSON.stringify(snapshot(), null, 2));
  await writer.close();
  return true;
}
export async function saveNamedColonyFile() {
  const directory = folderHandle || await restoreHandle();
  if (!directory) return false;
  if (typeof directory.requestPermission === "function" && await directory.requestPermission({ mode: "readwrite" }) !== "granted") return false;
  const file = await directory.getFileHandle(manualFile(), { create: true });
  const writer = await file.createWritable();
  await writer.write(JSON.stringify(snapshot(), null, 2));
  await writer.close();
  return true;
}
export function saveToSelectedFolder() {
  writing = writing.catch(() => {}).then(writeSelectedFolder);
  return writing;
}
export async function chooseColonyFolder() {
  if (typeof window.showDirectoryPicker !== "function") throw new Error("This browser does not support folder saving. Use Download Save instead.");
  const parent = await window.showDirectoryPicker({ mode: "readwrite" });
  folderHandle = await parent.getDirectoryHandle("Mars_Colony", { create: true });
  await rememberHandle(folderHandle);
  // Never overwrite an existing player save merely by reconnecting the folder.
  let existing = false;
  try {
    await folderHandle.getFileHandle(FILE);
    existing = true;
  } catch (e) {
    if (e.name !== "NotFoundError") throw e;
    const file = await folderHandle.getFileHandle(FILE, { create: true });
    const writer = await file.createWritable();
    await writer.write(JSON.stringify(snapshot(), null, 2));
    await writer.close();
  }
  return { existing };
}
export async function importColonySave(file) {
  const parsed = JSON.parse(await file.text());
  if (parsed?.format !== "EM2C-MARS-COLONY" || parsed.version !== 1 || !parsed.saves ||
      typeof parsed.saves !== "object") throw new Error("Not a valid EM²C save file");
  const base = parsed.saves["mars_em2c_save_v1"] || parsed.saves["mars_em2c_autosave_v1"];
  if (!base) throw new Error("Save file contains no colony state");
  const checked = JSON.parse(base);
  if (!Array.isArray(checked.buildings) || !checked.resources) throw new Error("Invalid colony data");
  // Preserve the previous browser save to permit recovery.
  for (const key of SAVE_KEYS) {
    const old = localStorage.getItem(key);
    if (old != null) localStorage.setItem("mars_before_file_import:" + key, old);
  }
  for (const key of SAVE_KEYS) {
    const next = parsed.saves[key];
    if (typeof next === "string") {
      JSON.parse(next);
      localStorage.setItem(key, next);
    } else localStorage.removeItem(key);
  }
  return true;
}
export async function disconnectColonyFolder() {
  folderHandle = null;
  const db = await openDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(HANDLE_KEY);
    tx.oncomplete = resolve;
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function listColonyFiles() {
  const directory = folderHandle || await restoreHandle();
  if (!directory) return [];
  if (typeof directory.requestPermission === "function" && await directory.requestPermission({ mode: "read" }) !== "granted") return [];
  const files = [];
  for await (const [name, handle] of directory.entries()) {
    if (handle.kind !== "file" || !name.endsWith(".json")) continue;
    const f = await handle.getFile();
    files.push({ name, modified: f.lastModified, size: f.size });
  }
  return files.sort((a,b) => b.modified - a.modified);
}
export async function loadNamedColonyFile(name) {
  if (!/^Mars_Colony_[A-Za-z0-9_.-]+\\.json$/.test(name)) throw new Error("Invalid save filename");
  const directory = folderHandle || await restoreHandle();
  if (!directory) throw new Error("Choose a save folder first");
  const handle = await directory.getFileHandle(name);
  await importColonySave(await handle.getFile());
  return true;
}
export async function loadFromSelectedFolder() {
  const directory = folderHandle || await restoreHandle();
  if (!directory) return false;
  if (typeof directory.requestPermission === "function") {
    const access = await directory.requestPermission({ mode: "read" });
    if (access !== "granted") return false;
  }
  const handle = await directory.getFileHandle(FILE);
  const file = await handle.getFile();
  await importColonySave(file);
  return true;
}

export function startPlayerAutosave(getGame) {
  // Save to browser storage frequently, not only when a new sol begins.
  let stopped = false;
  const timer = setInterval(() => {
    if (stopped) return;
    try {
      getGame().save?.("auto");
      saveToSelectedFolder().catch(err => console.warn("[EM2C] Folder autosave unavailable", err));
    } catch (err) { console.warn("[EM2C] Autosave failed", err); }
  }, 30000);
  return () => { stopped = true; clearInterval(timer); };
}
